import { useApi } from "@appretio/api";
import { ContextSelectedOptions } from "@appretio/context-selector";
import { useQuery } from "@tanstack/react-query";
import { TemporalContext } from "../../../components/temporalContextSelector/data/temporalContextOptions";
import { convertOptionsToApiFormat } from "../utils/convertToApiFormat";
import { colourChartQueryKeys } from "./queryKeys";

export interface ColourMetrics {
  /**
   * Hex code
   */
  style_colour_hex_code: string;
  /**
   * The main style color belonging to the colour hex code.
   */
  parent: string;
  /**
   * Total Sales (NIS3) in EUR.
   */
  nis3_sum: number;
  /**
   * Total Purchases (Time Purchase) in EUR.
   */
  time_purchase_sum: number;
}

export interface GroupedColourMetrics {
  /**
   * Display name of the colour group
   * BLACK, BLUE, etc.
   */
  name: string;
  /**
   * CSS compatible color for sunburst chart to show
   */
  colour: string;
  /**
   * The aggregated value for the colour group being either sales or purchases
   * depending on the selected column.
   */
  value: number;
  /**
   * The ratio of the colour group value to the total sum of all colour groups.
   * 0.34 means 34% of total sales/purchases is from this colour group.
   */
  ratio: number;
  /**
   * Children colours belonging to this colour group.
   * Only support one level hence no nested children.
   */
  children: Array<ColourMetrics & Omit<GroupedColourMetrics, "children">>;
}

export type KeysWithValsOfType<T, V> = keyof {
  [P in keyof T as T[P] extends V ? P : never]: P;
};

/**
 * Custom hook to fetch colour KPIs for In-Season Review Page.
 * Sales and Purchases grouped by colour hex code.
 */
export function useFetchColours(
  contextOptions: ContextSelectedOptions,
  temporalOptions: TemporalContext,
  enabled: boolean = false
) {
  const apiClient = useApi();

  return useQuery({
    queryKey: colourChartQueryKeys.data({ contextOptions, temporalOptions }),
    queryFn: async () => {
      const options = convertOptionsToApiFormat(
        contextOptions,
        temporalOptions
      );

      try {
        const response = await apiClient.POST(
          "/v1/in-season/review/get-review-data",
          {
            body: {
              ...options,
              query_params: {
                offset: 0,
                group: {
                  columns: ["style_colour_hex_code"]
                },
                aggregates: [
                  { column: "nis3", function: "sum", alias: "nis3_sum" },
                  {
                    column: "time_purchase",
                    function: "sum",
                    alias: "time_purchase_sum"
                  },
                  {
                    column: "style_main_colour",
                    function: "first",
                    alias: "parent"
                  }
                ]
              }
            }
          }
        );

        return (response.data?.result as ColourMetrics[]) ?? [];
      } catch (error) {
        console.error("Error fetching context options:", error);

        throw error; // Let TanStack Query handle the error state
      }
    },
    enabled, // Only fetch when enabled is true
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}
